/**
 * GardenTrails — Phase 5 PDP JavaScript
 * File: assets/gardentrails-phase5.js
 * Depends on: Heritage events.js (variant:update, cart:update)
 * Loaded via: <script src="..." defer> in sections/gt-pdp.liquid
 * Author: Claude (GardenTrails Project) · Phase 5 Session 2 · 10 April 2026
 *
 * Modules:
 *   1. Info-extras injection — appends .gt-pdp__info-extras into Heritage's
 *      .group-block-content (flex column), rendering below buy-buttons.
 *      Root cause of previous failure: .product-details is display:flex ROW,
 *      so appendChild there placed elements side-by-side. The correct target
 *      is .product-details .group-block .group-block-content (flex column).
 *   2. Variant price injection — injects per-variant price into Heritage tiles
 *   3. Variant heading override — replaces Heritage legend with custom label
 *   4. Tax / delivery note injection — injects below Heritage price block
 *   5. Offers slider dots — syncs scroll position to dot indicators
 *   6. Copy link — clipboard write + toast notification
 *   7. ATC success flash — green flash on cart:update event
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────────
     UTILITIES
  ───────────────────────────────────────────────────────────── */

  function formatMoney(cents) {
    const fmt = window.__gt_pdp_money_format || '₹{{amount}}';
    const rupees = Math.round(cents / 100);
    return fmt.replace('{{amount}}', rupees).replace('{{amount_no_decimals}}', rupees);
  }

  function debounce(fn, ms) {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
  }

  /* ─────────────────────────────────────────────────────────────
     1. INFO-EXTRAS INJECTION
     DOM structure of Heritage product-information:
       .product-details  (display:flex ROW — do NOT append here)
         .group-block    (Heritage "Details container" group block)
           .group-block-content  (display:flex COLUMN ← correct target)
             .group-block  (title + price group)
             .variant-picker
             .buy-buttons-block  ← our content goes AFTER this
     
     On desktop (≥990px): append extras into .group-block-content.
     On mobile: extras render naturally in DOM flow (below product-information).
     On resize: move back to original position.
  ───────────────────────────────────────────────────────────── */
  function initInfoExtrasInjection() {
    /* TWO-BLOCK INJECTION ARCHITECTURE
       Required layout:
         [Qty+ATC]          Heritage buy-buttons
         [BIN]              Heritage buy-buttons
         [WA + Offers]      gt-pdp__info-top  ← inject AFTER buy-buttons-block
         [Accordions]       Heritage native blocks (untouched)
         [Trust + Share]    gt-pdp__info-bottom ← inject at END of group-block-content
    */
    const infoTop    = document.querySelector('.gt-pdp__info-top');
    const infoBottom = document.querySelector('.gt-pdp__info-bottom');

    const infoColContent = document.querySelector(
      '.product-information .product-details .group-block .group-block-content'
    );
    if (!infoColContent) return;

    /* Inject info-top: after buy-buttons-block (before any accordions) */
    if (infoTop) {
      const buyButtons = infoColContent.querySelector('.buy-buttons-block');
      if (buyButtons) {
        buyButtons.insertAdjacentElement('afterend', infoTop);
      } else {
        infoColContent.insertBefore(infoTop, infoColContent.firstChild);
      }
      infoTop.classList.add('gt-pdp__info-top--injected');
    }

    /* Inject info-bottom: at END of column (after all Heritage blocks) */
    if (infoBottom) {
      infoColContent.appendChild(infoBottom);
      infoBottom.classList.add('gt-pdp__info-bottom--injected');
    }
  }

  /* ─────────────────────────────────────────────────────────────
     2. VARIANT PRICE INJECTION
     Heritage tiles render:
       <label class="variant-option__button-label">
         <input type="radio" ...>
         <span class="variant-option__button-label__pill"></span>
         <span class="variant-option__button-label__text">Pack of 1</span>
       </label>
     We inject <span class="gt-pdp__vt-price">₹299</span> after __text span.
  ───────────────────────────────────────────────────────────── */
  function initVariantPriceInjection() {
    const sectionId = window.__gt_pdp_section_id;
    const variants  = window['__gt_pdp_variants_' + sectionId];
    if (!variants || !variants.length) return;

    const variantMap = {};
    variants.forEach(v => {
      if (v.title) variantMap[v.title] = v;
      if (v.options) v.options.forEach(opt => { if (!variantMap[opt]) variantMap[opt] = v; });
    });

    function injectPrices() {
      document.querySelectorAll('.product-information .variant-option__button-label__text').forEach(textEl => {
        const label = textEl.closest('.variant-option__button-label');
        if (!label) return;

        const existing = label.querySelector('.gt-pdp__vt-price');
        if (existing) existing.remove();

        const optionValue = textEl.textContent.trim();
        const variant = variantMap[optionValue];
        if (!variant) return;

        const priceEl = document.createElement('span');
        priceEl.className = 'gt-pdp__vt-price';
        priceEl.setAttribute('aria-hidden', 'true');
        priceEl.textContent = formatMoney(variant.price);
        textEl.insertAdjacentElement('afterend', priceEl);
      });
    }

    injectPrices();
    document.addEventListener('variant:update', () => setTimeout(injectPrices, 60));
  }

  /* ─────────────────────────────────────────────────────────────
     3. VARIANT HEADING OVERRIDE
     CSS hides Heritage's <legend>. JS injects our custom heading
     before each fieldset with selected value sublabel.
  ───────────────────────────────────────────────────────────── */
  function initVariantHeading() {
    const variantLabel = window.__gt_pdp_variant_label || 'SELECT PACK SIZE';

    function updateHeading() {
      const fieldsets = document.querySelectorAll('.product-information .variant-option--buttons');
      if (!fieldsets.length) return;

      fieldsets.forEach(fieldset => {
        const prev = fieldset.previousElementSibling;
        if (prev && prev.classList.contains('gt-pdp__variant-heading')) prev.remove();

        const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
        const selectedValue = checkedInput ? checkedInput.value : '';

        const heading = document.createElement('div');
        heading.className = 'gt-pdp__variant-heading';
        heading.innerHTML = variantLabel
          + (selectedValue
              ? ' <span class="gt-pdp__variant-sublabel">\u2014 ' + selectedValue + ' selected</span>'
              : '');

        fieldset.insertAdjacentElement('beforebegin', heading);
      });
    }

    updateHeading();
    document.addEventListener('variant:update', () => setTimeout(updateHeading, 60));
    document.querySelectorAll('.product-information .variant-option--buttons input[type="radio"]')
      .forEach(input => input.addEventListener('change', () => setTimeout(updateHeading, 30)));
  }

  /* ─────────────────────────────────────────────────────────────
     4. TAX / DELIVERY NOTE INJECTION
     Injects note below Heritage's price [ref="priceContainer"].
  ───────────────────────────────────────────────────────────── */
  function initTaxDeliveryNote() {
    const showTax      = window.__gt_pdp_show_tax_note;
    const showDelivery = window.__gt_pdp_show_delivery_note;
    const threshold    = window.__gt_pdp_threshold;
    if (!showTax && !showDelivery) return;

    const priceContainer = document.querySelector('.product-information [ref="priceContainer"]');
    if (!priceContainer) return;

    if (document.querySelector('.gt-pdp__price-note')) return;

    const parts = [];
    if (showTax)      parts.push('Incl. of all taxes');
    if (showDelivery) parts.push('\uD83D\uDE9A Free delivery on orders above \u20B9' + threshold);

    const note = document.createElement('p');
    note.className = 'gt-pdp__price-note';
    note.textContent = parts.join(' \u00B7 ');
    priceContainer.insertAdjacentElement('afterend', note);
  }

  /* ─────────────────────────────────────────────────────────────
     5. OFFERS SLIDER DOTS
  ───────────────────────────────────────────────────────────── */
  function initOffersSlider() {
    const sectionId = window.__gt_pdp_section_id;
    const scroll    = document.getElementById('gt-offers-scroll-' + sectionId);
    const dotsWrap  = document.getElementById('gt-offers-dots-' + sectionId);
    if (!scroll || !dotsWrap) return;

    const dots  = Array.from(dotsWrap.querySelectorAll('.gt-pdp__o-dot'));
    const cards = Array.from(scroll.querySelectorAll('.gt-pdp__offer-card'));
    if (!dots.length || !cards.length) return;

    function updateDots() {
      const cardWidth = cards[0].offsetWidth + 10;
      const idx = Math.max(0, Math.min(Math.round(scroll.scrollLeft / cardWidth), dots.length - 1));
      dots.forEach((d, i) => d.classList.toggle('gt-pdp__o-dot--on', i === idx));
    }

    scroll.addEventListener('scroll', debounce(updateDots, 50), { passive: true });
    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        scroll.scrollTo({ left: (cards[0].offsetWidth + 10) * i, behavior: 'smooth' });
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     6. COPY LINK
  ───────────────────────────────────────────────────────────── */
  function initCopyLink() {
    const sectionId = window.__gt_pdp_section_id;
    const toast     = document.getElementById('gt-copy-toast-' + sectionId);
    const btn       = document.querySelector('[data-gt-copy-link]');
    if (!btn) return;

    let toastTimer;
    function showToast() {
      if (!toast) return;
      toast.classList.add('gt-pdp__copy-toast--show');
      clearTimeout(toastTimer);
      toastTimer = setTimeout(() => toast.classList.remove('gt-pdp__copy-toast--show'), 2000);
    }

    btn.addEventListener('click', () => {
      const url = window.location.href;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(showToast).catch(() => window.prompt('Copy link:', url));
      } else {
        window.prompt('Copy link:', url);
      }
    });
  }

  /* ─────────────────────────────────────────────────────────────
     7. ATC SUCCESS FLASH
  ───────────────────────────────────────────────────────────── */
  function initAtcFlash() {
    document.addEventListener('cart:update', (event) => {
      const data = event.detail && event.detail.data;
      if (!data || data.didError) return;

      const atcBtn = document.querySelector('.product-information .add-to-cart-button');
      if (!atcBtn || atcBtn.disabled) return;

      const origHTML = atcBtn.innerHTML;
      const origBg   = atcBtn.style.background;
      atcBtn.textContent = '\u2713 Added!';
      atcBtn.style.setProperty('background', 'var(--gt-fern)', 'important');
      atcBtn.disabled = true;

      setTimeout(() => {
        atcBtn.innerHTML = origHTML;
        atcBtn.style.background = origBg;
        atcBtn.disabled = false;
      }, 1800);
    });
  }

  /* ─────────────────────────────────────────────────────────────
     8. TAB SWITCHING
     Manages Expert Tabs: click activates panel, updates ARIA,
     animates Mustard underline via .gt-pdp__tab-btn--active class.
     One panel visible at a time.
  ───────────────────────────────────────────────────────────── */
  function initTabSwitching() {
    const tabsSection = document.querySelector('.gt-pdp__tabs-section');
    if (!tabsSection) return;

    const buttons = Array.from(tabsSection.querySelectorAll('.gt-pdp__tab-btn'));
    const panels  = Array.from(tabsSection.querySelectorAll('.gt-pdp__tab-pane'));
    if (!buttons.length || !panels.length) return;

    function activateTab(targetId) {
      buttons.forEach(btn => {
        const isTarget = btn.dataset.gtTab === targetId;
        btn.classList.toggle('gt-pdp__tab-btn--active', isTarget);
        btn.setAttribute('aria-selected', isTarget ? 'true' : 'false');
      });
      panels.forEach(panel => {
        const isTarget = panel.id === 'gt-tab-pane-' + targetId;
        panel.classList.toggle('gt-pdp__tab-pane--active', isTarget);
      });
    }

    buttons.forEach(btn => {
      btn.addEventListener('click', () => activateTab(btn.dataset.gtTab));
    });

    /* Keyboard: left/right arrows navigate tabs */
    tabsSection.querySelector('.gt-pdp__tabs-nav')
      .addEventListener('keydown', (e) => {
        const idx = buttons.findIndex(b => b === document.activeElement);
        if (idx === -1) return;
        if (e.key === 'ArrowRight') {
          const next = buttons[(idx + 1) % buttons.length];
          next.focus();
          activateTab(next.dataset.gtTab);
          e.preventDefault();
        }
        if (e.key === 'ArrowLeft') {
          const prev = buttons[(idx - 1 + buttons.length) % buttons.length];
          prev.focus();
          activateTab(prev.dataset.gtTab);
          e.preventDefault();
        }
      });
  }

  /* ─────────────────────────────────────────────────────────────
     9. FAQ ACCORDION
     One-open-at-a-time. Smooth max-height animation via CSS.
     Updates aria-expanded on button. Keyboard accessible.
  ───────────────────────────────────────────────────────────── */
  function initFaqAccordion() {
    const faqSection = document.querySelector('.gt-pdp__faq');
    if (!faqSection) return;

    const items = Array.from(faqSection.querySelectorAll('.gt-pdp__faq-item'));
    if (!items.length) return;

    function openItem(item) {
      /* Close all others first */
      items.forEach(i => {
        if (i !== item) {
          i.classList.remove('gt-pdp__faq-item--open');
          const btn = i.querySelector('.gt-pdp__faq-q');
          if (btn) btn.setAttribute('aria-expanded', 'false');
        }
      });
      /* Toggle target */
      const isOpen = item.classList.contains('gt-pdp__faq-item--open');
      item.classList.toggle('gt-pdp__faq-item--open', !isOpen);
      const btn = item.querySelector('.gt-pdp__faq-q');
      if (btn) btn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    }

    items.forEach(item => {
      const btn = item.querySelector('.gt-pdp__faq-q');
      if (!btn) return;
      btn.addEventListener('click', () => openItem(item));
    });
  }

  /* ─────────────────────────────────────────────────────────────
     INIT
  ───────────────────────────────────────────────────────────── */
  /* ─────────────────────────────────────────────────────────────
     10. BREADCRUMB INJECTION
     gt-pdp section now renders AFTER product-information in product.json
     (so tabs/premium/FAQ appear correctly below the gallery).
     The breadcrumb must visually appear ABOVE the gallery, so we
     inject it before the product-information section on DOM load.
  ───────────────────────────────────────────────────────────── */
  function initBreadcrumbInjection() {
    const breadcrumb = document.querySelector('.gt-pdp__breadcrumb-strip');
    if (!breadcrumb) return;

    const productInfo = document.querySelector('.product-information');

    if (productInfo) {
      /* Move breadcrumb above gallery — it is display:none until shown here */
      productInfo.insertAdjacentElement('beforebegin', breadcrumb);
    }
    /* Always show breadcrumb — whether injected above gallery or in fallback position */
    breadcrumb.style.display = 'block';
  }

  function init() {
    initBreadcrumbInjection();
    initInfoExtrasInjection();
    initVariantPriceInjection();
    initVariantHeading();
    initTaxDeliveryNote();
    initOffersSlider();
    initCopyLink();
    initAtcFlash();
    initTabSwitching();
    initFaqAccordion();
    initCouponCopy();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ── COUPON COPY — Tap to Copy clipboard handler ── */
  function initCouponCopy() {
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('[data-gt-copy]');
      if (!btn) return;

      var code = btn.getAttribute('data-gt-copy');
      if (!code) return;

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(function() {
          showCopied(btn);
        }).catch(function() { fallbackCopy(code, btn); });
      } else {
        fallbackCopy(code, btn);
      }
    });
  }

  function fallbackCopy(code, btn) {
    var el = document.createElement('textarea');
    el.value = code;
    el.style.cssText = 'position:fixed;opacity:0;top:0;left:0';
    document.body.appendChild(el);
    el.focus(); el.select();
    try { document.execCommand('copy'); showCopied(btn); } catch(err) {}
    document.body.removeChild(el);
  }

  function showCopied(btn) {
    var label = btn.querySelector('.gt-pdp__oc-tap-label');
    var copyIcon = btn.querySelector('.gt-pdp__oc-copy-icon');
    var checkIcon = btn.querySelector('.gt-pdp__oc-check-icon');
    btn.classList.add('gt-pdp__oc-tap--copied');
    if (label) label.textContent = 'Copied!';
    if (copyIcon) copyIcon.style.display = 'none';
    if (checkIcon) checkIcon.style.display = '';
    setTimeout(function() {
      btn.classList.remove('gt-pdp__oc-tap--copied');
      if (label) label.textContent = 'Tap to copy';
      if (copyIcon) copyIcon.style.display = '';
      if (checkIcon) checkIcon.style.display = 'none';
    }, 1800);
  }


})();
